import type { RouteRecordRaw, NavigationGuardNext, RouteLocationNormalized } from 'vue-router';
import { isDemoMode, LocalStorageKey, LocalStorageService } from '@template/utils';
import { useGlobalDialogManager } from '@/composables/useGlobalDialogManager';
import MainLayout from '@/components/layout/MainLayout.vue';
import { createRouter, createWebHistory } from 'vue-router';
import { useSignupStore } from '@/stores/useSignupStore';
import { useUserStore } from '@/stores/useUserStore';

const hasFirstHomeEntry = () => {
  return LocalStorageService.getItem(LocalStorageKey.FIRST_HOME_ENTRY_DONE) === 'true';
};

const markFirstHomeEntryDone = () => {
  LocalStorageService.setItem(LocalStorageKey.FIRST_HOME_ENTRY_DONE, 'true');
};

// 커스텀 메타 타입 정의
interface CustomRouteMeta {
  layout?: typeof MainLayout;
  auth?: boolean;
  footer?: boolean; // footer 표시 여부 (기본값: true)
  /** true이면 헤더를 투명 오버레이로 표시하고 main 상단 여백을 제거 */
  headerOverlay?: boolean;
  /** true이면 sign-up에서 타입 선택 후에만 진입 가능 (회원가입 중간 스텝) */
  requiresSignUpType?: boolean;
  /** true이면 개발 모드에서만 접근 가능 (배포 환경에서는 홈으로 리다이렉트) */
  devOnly?: boolean;
}

// 라우트 타입을 커스텀 메타로 확장
type CustomRouteRecordRaw = RouteRecordRaw & {
  meta?: CustomRouteMeta;
  children?: CustomRouteRecordRaw[];
};

const routes: CustomRouteRecordRaw[] = [
  {
    path: '/auth',
    children: [
      {
        path: 'login',
        name: 'login',
        component: () => import('@/views/auth/login/Index.vue'),
      },

      {
        path: 'sign-up',
        children: [
          {
            path: '',
            name: 'sign-up',
            component: () => import('@/views/auth/signup/Index.vue'),
          },
          {
            path: 'individual',
            name: 'individual-sign-up',
            meta: { requiresSignUpType: true },
            component: () => import('@/views/auth/signup/individual/Index.vue'),
          },
          {
            path: 'corporate',
            name: 'corporate-sign-up',
            meta: { requiresSignUpType: true },
            component: () => import('@/views/auth/signup/corporate/Index.vue'),
          },
          {
            path: 'complete',
            name: 'sign-up-complete',
            meta: { requiresSignUpType: true },
            component: () => import('@/views/auth/signup/complete/Index.vue'),
          },
        ],
      },
      {
        path: 'find-id',
        name: 'find-id',
        component: () => import('@/views/auth/findId/Index.vue'),
      },
      {
        path: 'reset-password',
        children: [
          {
            path: '',
            name: 'reset-password',
            component: () => import('@/views/auth/resetPassword/Index.vue'),
          },
          {
            path: 'complete',
            name: 'reset-password-complete',
            component: () => import('@/views/auth/resetPassword/complete/Index.vue'),
          },
        ],
      },
    ],
  },
  {
    path: '/document',
    children: [
      {
        path: 'individual',
        name: 'individual-document',
        component: () => import('@/views/auth/document/individual/Index.vue'),
      },
      {
        path: 'corporate',
        name: 'corporate-document',
        component: () => import('@/views/auth/document/corporate/Index.vue'),
      },
      {
        path: 'complete',
        name: 'document-complete',
        component: () => import('@/views/auth/document/complete/Index.vue'),
      },
    ],
  },
  {
    path: '/',
    name: 'root',
    redirect: () => (hasFirstHomeEntry() ? { name: 'order' } : { name: 'home' }),
  },
  {
    path: '/home',
    name: 'home',
    meta: { layout: MainLayout, headerOverlay: true },
    component: () => import('@/views/home/Index.vue'),
  },
  {
    path: '/order',
    name: 'order',
    meta: { layout: MainLayout, footer: false },
    component: () => import('@/views/order/Index.vue'),
  },
  {
    path: '/transaction/:transactionTab?',
    name: 'transaction',
    meta: { layout: MainLayout, auth: true },
    component: () => import('@/views/transaction/Index.vue'),
  },
  {
    path: '/asset',
    name: 'asset',
    meta: { layout: MainLayout, auth: true },
    component: () => import('@/views/assets/Index.vue'),
  },
  {
    path: '/accounts/:accountManagementTab?',
    name: 'account-management',
    meta: { layout: MainLayout, auth: true },
    component: () => import('@/views/accountManagement/Index.vue'),
  },
  {
    path: '/support/:supportTab?',
    name: 'support',
    meta: { layout: MainLayout },
    component: () => import('@/views/support/Index.vue'),
  },
  {
    path: '/privacy-policy',
    name: 'privacy-policy',
    meta: { layout: MainLayout },
    component: () => import('@/views/privacyPolicy/Index.vue'),
  },
  {
    path: '/mypage/:menuTab?',
    name: 'mypage',
    meta: { layout: MainLayout, auth: true },
    component: () => import('@/views/myPage/Index.vue'),
  },
  {
    path: '/markup',
    name: 'markup',
    meta: { devOnly: true },
    component: () => import('@/views/markup/Index.vue'),
  },
  {
    path: '/kcb/callback',
    name: 'kcb-callback',
    component: () => import('@/views/kcb/Index.vue'),
    meta: {},
  },
  {
    path: '/error',
    name: 'error',
    meta: { layout: MainLayout },
    component: () => import('@/views/common/error/Index.vue'),
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    meta: { layout: MainLayout },
    component: () => import('@/views/common/notFound/Index.vue'),
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition;
    if (to.hash) return { el: to.hash, behavior: 'smooth' };
    return { left: 0, top: 0 };
  },
});

// 로그인 확인은 공통 컴포저블의 ensureLoggedIn을 사용

/**
 * 네비게이션 가드
 * 인증 및 리다이렉트 규칙을 처리합니다.
 */
router.beforeEach(
  (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
    const userStore = useUserStore();
    const dialogManager = useGlobalDialogManager();
    const ensureLoggedIn = dialogManager.ensureLoggedIn;
    const loggedIn = userStore.checkLoggedIn();

    // 최초 1회만 home 진입 허용, 이후 home 접근은 로그인 여부와 무관하게 order로 고정
    if (to.name === 'home') {
      const alreadyEntered = hasFirstHomeEntry();
      const isInitialNavigation = from.matched.length === 0;

      // 요구사항: "최초 URL 접근"에서만 home을 보이고, 이후 URL 진입(새 진입/새로고침 등)에서는 무조건 order로 리다이렉트
      // 반면, 내부 네비게이션(탭 클릭)으로 home에 들어오는 경우에는 리다이렉트하지 않도록 처리
      if (alreadyEntered && isInitialNavigation) {
        return next({ name: 'order', replace: true });
      }

      if (isInitialNavigation && !alreadyEntered) {
        markFirstHomeEntryDone();
      }
    }

    // devOnly 라우트: 배포 환경에서는 홈으로 리다이렉트
    if (to.meta?.devOnly && !import.meta.env.DEV) {
      return next({ name: 'home', replace: true });
    }

    // 로그인 상태에서 인증 페이지 접근 차단
    if (loggedIn && to.path.startsWith('/auth')) {
      return next({ name: 'home' });
    }

    // 회원가입 중간 스텝 직접 진입 차단 (sign-up에서 타입 선택 후에만 진입 가능)
    if (to.meta?.requiresSignUpType) {
      const signupStore = useSignupStore();
      if (signupStore.signUpType === undefined) {
        return next({ name: 'sign-up' });
      }
    }

    // 인증이 필요한 페이지인지 확인
    if (to.meta?.auth) {
      if (!loggedIn) {
        // 특정 라우트는 로그인 페이지로 리다이렉트
        if (to.name === 'mypage') {
          return next({ name: 'login' });
        }
        // 그 외에는 모달 표시 후 네비게이션 취소
        ensureLoggedIn();
        return next(false);
      }
    }

    // Open API 관리 페이지 접근 제한
    if (to.name === 'mypage' && to.params.menuTab === 'open-api-management') {
      const isDirectUrlEntry = from.name !== 'mypage';
      const redirectToNotification = () =>
        next({ name: 'mypage', params: { menuTab: 'notification' } });
      const blockNavigation = () => next(false);

      if (isDemoMode()) {
        dialogManager.open('openApiLoginNeeded');
        return isDirectUrlEntry ? redirectToNotification() : blockNavigation();
      }
    }

    if (
      from.name === 'account-management' &&
      from.query.state === 'new' &&
      (to.name !== 'account-management' || to.params.accountManagementTab !== 'create')
    ) {
      // 계좌 개설 중 이탈 확인 다이얼로그 표시
      dialogManager.open('accountCreateLeave', (payload) => {
        if (payload.confirmed) {
          // 확인 시 라우트 이동 허용
          next();
        } else {
          // 취소 시 라우트 이동 차단
          next(false);
        }
      });
      return;
    }

    next();
  }
);

export default router;
